{
  "reports": {
    "navigation_title": "Reports",
    "create_new": "New Report",
    "devices": "Devices",
    "parameters": "Parameters",
    "common_parameters": {
      "title": "Select across devices",
      "devices": "%{count}/%{total} devices"
    },
    "floor_negative_deltas": "Floor negative deltas at zero",
    "floor_negative_deltas_tooltip": "When enabled, a downwind − upwind delta below zero (upwind reading higher than downwind) is reported as zero for concentration parameters. Signed parameters such as differential pressure and temperature are never floored.",
    "aggregate": "Aggregation",
    "aggregation_info": {
      "subheader": "How measurements are grouped before the report evaluates them",
      "metrics_warning": "Statistical parameters such as a 15-minute rolling average are already averaged over their own period. Aggregation averages them again and hides short peaks.",
      "intro": "Sets whether measurements are used as recorded or averaged into fixed intervals.",
      "none_title": "None",
      "none": [
        "Measurements are used as recorded, so short peaks stay visible.",
        "Long periods or many devices may be too large to generate; then shorten the range or choose an interval."
      ],
      "intervals_title": "Intervals: %{intervals}",
      "intervals": [
        "Each interval shows the arithmetic mean of its measurements.",
        "Averaging smooths noise and shortens reports, but hides peaks shorter than the interval.",
        "**Applying an interval to statistical parameters like AVG (rolling average) averages them again.**"
      ],
      "types": {
        "aethair_cms_2567": {
          "title": "CMS-2567 reports",
          "items": [
            "A failing interval counts with its full length toward the violation duration.",
            "Offline time is estimated from missing intervals; without aggregation, 1-minute measurements are assumed."
          ]
        }
      }
    },
    "title": "Reports",
    "new_title": "Create Report",
    "edit_title": "Edit Report",
    "clone_title": "Clone Report",
    "preview_title": "Preview Report",
    "view_title": "View Report",
    "analytics_title": "Analyze Report",
    "common": "Place info",
    "name": "Name",
    "description": "Description",
    "phone": "Phone",
    "address": "Address",
    "wind_type": "Wind Type",
    "download_csv": "Download CSV",
    "download_xlsx": "Download Excel",
    "place_picture": "Picture of the place",
    "scheduled_daily": "Scheduled Daily",
    "scheduled_weekly": "Scheduled Weekly",
    "job_statuses": {
      "html_upload_failed": "HTML upload failed",
      "pdf_upload_failed": "PDF upload failed",
      "email_delivery_failed": "Email delivery failed",
      "generic_failed": "%{key} failed"
    },
    "assets_statuses": {
      "preparing_title": "Preparing your report…",
      "preparing_sub": "This usually takes less than a minute. You can leave this page — generation continues in the background.",
      "title_ready": "Your report is ready",
      "title_one_failed": "One asset failed to generate",
      "title_some_failed": "Some assets failed to generate",
      "csv": "CSV",
      "html": "HTML",
      "pdf": "PDF",
      "noesis_summary": "Summary by Noesis",
      "in_progress": "In progress",
      "error_csv": "CSV export failed",
      "error_html": "HTML upload failed",
      "error_pdf": "PDF upload failed",
      "error_noesis": "Summary generation failed",
      "scheduled_next_run_text": "Next report run is scheduled for %{date} (%{timezone}).",
      "scheduled_waiting_text": "Report is scheduled. Please check back later.",
      "next_run_at": "Next report run is scheduled for %{date} (%{timezone}).",
      "scheduled": {
        "first_run_title": "First report runs",
        "frequency_label": "Frequency",
        "range_label": "Time range",
        "range_daily": "Last 24 hours",
        "range_weekly": "Last 7 days",
        "recipients_label": "Recipients",
        "no_recipients": "—"
      }
    },
    "wind_detection_types": {
      "auto": "Auto",
      "manual": "Manual"
    },
    "device_type": {
      "upwind": "Upwind",
      "downwind": "Downwind"
    },
    "frequency": {
      "daily": "Daily",
      "weekly": "Weekly"
    },
    "dialogs": {
      "delete": {
        "title": "Are you sure?",
        "content": "Are you sure you want to delete '%{name}' report? You can't undo this action"
      }
    },
    "edit_details_breadcrumb": "Edit",
    "configuration_breadcrumb": "Configuration",
    "view_details_breadcrumb": "View",
    "analytics_details_breadcrumb": "Analytics",
    "parameter_mixed_units": "All parameters in this row must use the same unit (selected: %{units})",
    "rolling": {
      "mixed_types": "Without aggregation select either AVG (rolling average) statistical parameters or other parameters, not both",
      "mixed_periods": "All statistical parameters must use the same averaging period (selected: %{periods} min)"
    },
    "perimeter_validation": {
      "single_device": "Select this parameter on at least two devices so upwind and downwind can be compared",
      "missing_wind_types": "Select this parameter on the %{types} side as well"
    },
    "perimeter_custom": {
      "info": {
        "subheader": "Custom perimeter monitoring with your own alert levels",
        "description": "Compares downwind with upwind (background) readings using the parameters and alert levels you choose. Use it for site-specific limits, other regulations (for example SCAQMD Rule 1467, VOC 0.5 ppm) or internal targets.",
        "setup": {
          "parameters_title": "Parameters",
          "parameters": [
            "Select the same parameter on the upwind and downwind devices you want to compare: raw sensor channels or statistical parameters.",
            "Without aggregation, select either AVG (rolling average) statistical parameters with one period or other parameters, not both.",
            "Optionally add one or two alert levels, each with an operator and a value. Parameters without one are reported without highlighting or alert periods.",
            "Values and alert levels use the unit and precision of the selected parameters."
          ],
          "wind_title": "Wind",
          "wind": [
            "With aggregation, wind is averaged per interval and upwind and downwind are chosen per interval.",
            "Without aggregation, wind is averaged every minute over the AVG (rolling average) period or 15 minutes (at least 3 readings); during calm wind (below 0.5 m/s) the previous selection is kept."
          ]
        },
        "calculation": {
          "intro": "Evaluation depends on the parameter type and the aggregation.",
          "types_title": "By parameter type",
          "raw_title": "Raw sensor channels",
          "raw": [
            "With aggregation: readings in each interval are averaged into one value per device.",
            "Without aggregation: readings are evaluated every minute as recorded. Each table row covers one reporting interval and shows its last readings. Wind is averaged over the previous 15 minutes.",
            "Best for catching short spikes, but readings can be noisy and long periods give long tables (600 rows for a 10-hour day at 1-minute reporting)."
          ],
          "avg_title": "AVG (rolling average) statistical parameters",
          "avg": [
            "A rolling average recalculated every minute over its period, for example a 15-minute rolling average.",
            "With aggregation: the rolling averages in each interval are averaged again, which smooths peaks further.",
            "Without aggregation: evaluated every minute as is. Each table row covers one period and shows its last rolling average (the 09:59 value on the 09:45 row). All AVG (rolling average) statistical parameters must share one period, which also sets the wind window."
          ],
          "other_title": "Other statistical parameters (MIN, MAX, ABS, DIFF)",
          "other": [
            "With aggregation: their values in each interval are averaged, so MAX 15 min gives the mean of its rolling maxima, not the interval's highest value.",
            "Without aggregation: used as recorded, like raw sensor channels."
          ],
          "steps_title": "Evaluation steps",
          "aggregated_title": "With aggregation",
          "steps": [
            "Each parameter is averaged per interval and device.",
            "Upwind (background) and downwind values are taken per interval, based on the wind detection.",
            "Delta = downwind − upwind. With \"Floor negative deltas at zero\" enabled, a negative delta counts as 0.",
            "Intervals whose delta breaches an alert level are highlighted: amber for alert level 1 of two, red for alert level 2 or a single level. Consecutive breaching intervals form an alert period with its start, duration and peak delta."
          ],
          "rolling_title": "Without aggregation",
          "rolling_steps": [
            "Every minute, each parameter's value is taken per device: the rolling average or the recorded reading.",
            "Upwind (background) and downwind values are taken per minute, based on the wind detection.",
            "Delta = downwind − upwind. With \"Floor negative deltas at zero\" enabled, a negative delta counts as 0.",
            "Consecutive breaching minutes form an alert period with its start, duration and peak delta.",
            "Table rows are highlighted when their delta breaches an alert level (amber for alert level 1 of two, red for alert level 2 or a single level); shorter alert periods inside a row appear in the Impact Assessment."
          ]
        }
      }
    },
    "alert_levels": {
      "add": "Add alert level",
      "add_level": "Add alert level %{index}",
      "level": "Alert level %{index}",
      "name": "Name",
      "value": "Value",
      "errors": {
        "required": "Required",
        "number": "Must be a number",
        "positive": "Must be greater than 0",
        "higher": "Must be higher than alert level %{index}",
        "lower": "Must be lower than alert level %{index}"
      }
    },
    "perimeter_standard": {
      "metrics_label": "15-minute rolling averages",
      "no_metrics": "No 15-minute rolling average %{pollutant} statistical parameters on the selected devices",
      "missing_wind_types": "Select a statistical parameter from the %{types} side as well",
      "mixed_units": "All statistical parameters of this row must use the same unit (selected: %{units})",
      "pollutants": {
        "pm10": "Particulates (PM10)",
        "voc": "Organic vapors (VOC)"
      },
      "additional_parameter": "Additional parameter",
      "other_parameters": "other",
      "add_parameter": "Add parameter",
      "pollutant_names": {
        "pm10": "Particulates",
        "voc": "Organic vapors"
      },
      "pollutant_titles": {
        "pm10": "Particulates",
        "voc": "Organic vapors (VOC)"
      }
    },
    "der10": {
      "info": {
        "issued": "Issued on %{date} by the NYS Department of Environmental Conservation, Division of Environmental Remediation.",
        "description": "Appendices 1A (NYSDOH Generic Community Air Monitoring Plan) and 1B require real-time monitoring of VOCs and PM10 at the downwind perimeter during ground-intrusive work. Downwind levels are compared with upwind (background) levels as 15-minute averages; exceedances require dust suppression, a work pause or a shutdown.",
        "open_pdf": "Open DER-10 (PDF)",
        "tabs": {
          "overview": "Overview",
          "setup": "Setup",
          "calculation": "Calculation"
        },
        "setup": {
          "parameters_title": "Parameters",
          "parameters": [
            "Only 15-minute rolling averages are accepted: of a PM10 or VOC channel, or of a DustTrak \"Mass Conc. Total\" channel with a PM10 inlet.",
            "Create one per device and pollutant on the device page: Add statistical parameter → Function: Average, Period: 15 min, Parameter: PM10 (or VOC).",
            "They're calculated from the moment they're created, so create them before intrusive work starts.",
            "Select them on every device in the report. PM10 alone is valid; VOC is optional.",
            "The PM10 and VOC rows are pre-filled with the DER-10 levels. You can change them and set one or two alert levels per row.",
            "Add parameters for other 15-minute rolling averages (for example H₂S) with their own alert levels.",
            "Levels are compared with the readings in the parameters' unit, which is shown next to each level. DER-10 states its levels in µg/m³ (PM10) and ppm (VOC): adjust them for parameters in another unit (100 µg/m³ = 0.1 mg/m³, 5 ppm = 5000 ppb). All parameters of one row must use the same unit."
          ],
          "wind_title": "Wind",
          "wind": [
            "Automatic wind detection: select the raw Wind Speed and Wind Direction (°) channels. Wind is averaged over the same 15 minutes (at least 3 readings); during calm wind (below 0.5 m/s) the previous selection is kept.",
            "Manual wind detection: each pollutant needs an upwind and a downwind statistical parameter, matching the fixed station layout DER-10 describes."
          ]
        },
        "calculation": {
          "intro": "The report evaluates every minute of the reporting period:",
          "steps": [
            "Each statistical parameter provides a 15-minute rolling average, updated every minute.",
            "Upwind (background) and downwind values are taken per minute, based on the wind detection.",
            "Delta = downwind − upwind. Negative deltas (upwind higher than downwind) are reported as measured.",
            "The delta is compared with each row's alert levels. The DER-10 defaults are PM10 ≥ 100 µg/m³ (dust suppression) and > 150 µg/m³ (stop work); VOC > 5 ppm (pause) and > 25 ppm (shut down).",
            "Consecutive minutes above a level form one exceedance, with its start, end, duration and peak delta.",
            "The data table has one row per 15 minutes with the rolling average at the last minute (the 09:59 value on the 09:45 row). Shorter exceedances appear in the Impact Assessment.",
            "Minutes without data from both sides are not evaluated."
          ]
        }
      },
      "level_names": {
        "pm10": [
          "Apply dust suppression",
          "Stop work and re-evaluate"
        ],
        "voc": [
          "Pause work",
          "Shut down"
        ]
      },
      "level_info": {
        "pm10": [
          "DER-10: apply dust suppression at ≥ 100 µg/m³ above upwind",
          "DER-10: stop work and re-evaluate at > 150 µg/m³ above upwind"
        ],
        "voc": [
          "DER-10: pause work at > 5 ppm above background",
          "DER-10: shut down at > 25 ppm above background"
        ]
      }
    },
    "nj_pam": {
      "info": {
        "issued": "Version 1.0, issued in %{date} by the New Jersey Department of Environmental Protection, Contaminated Site Remediation & Redevelopment.",
        "description": "N.J.A.C. 7:26E-5.5(b)7 requires a perimeter air monitoring plan for remedial actions longer than 20 working days. PM10 and total VOCs are monitored in real time at the perimeter as 15-minute time-weighted averages and compared with site-specific response levels derived from the NJDEP PAM Calculator. Exceedances lasting 15, 30, 45 and 60 minutes require checks, emission controls, confirmatory sampling and stopping work.",
        "open_pdf": "Open PAM Technical Guidance (PDF)",
        "setup": {
          "parameters_title": "Parameters",
          "parameters": [
            "Only 15-minute rolling averages are accepted: of a PM10 or VOC channel, or of a DustTrak \"Mass Conc. Total\" channel with a PM10 inlet.",
            "Create one per device and pollutant on the device page: Add statistical parameter → Function: Average, Period: 15 min, Parameter: PM10 (or VOC).",
            "They're calculated from the moment they're created, so create them before emission-generating work starts.",
            "PM10 is pre-filled with 150, the 24-hour PM10 NAAQS in µg/m³. Enter a lower site-specific response level if the PAM plan requires one; it shouldn't exceed 150 µg/m³.",
            "Enter the site-specific TVOC response level (ppb) from the PAM plan: the guidance has no default.",
            "Add parameters for chemical-specific monitors (for example mercury vapor or H₂S) and use their HBTV as the alert level.",
            "Levels are compared with the readings in the parameters' unit, which is shown next to each level: adjust the PM10 level for parameters in another unit (150 µg/m³ = 0.15 mg/m³). All parameters of one row must use the same unit."
          ],
          "wind_title": "Wind",
          "wind": [
            "The guidance requires site-specific wind speed and direction; public weather stations can't be the only source.",
            "Automatic wind detection: select the raw Wind Speed and Wind Direction (°) channels. Wind is averaged over the same 15 minutes (at least 3 readings); during calm wind (below 0.5 m/s) the previous selection is kept.",
            "Manual wind detection: each pollutant needs at least one upwind and one downwind statistical parameter."
          ]
        },
        "calculation": {
          "intro": "The report evaluates each 15-minute monitoring interval of the reporting period:",
          "steps": [
            "Each statistical parameter provides a 15-minute rolling average, updated every minute. The average at an interval's last minute (e.g. 09:59 for 09:45–09:59) is the interval's time-weighted average.",
            "Upwind (background) and downwind values are taken at that minute, based on the wind detection.",
            "Delta = downwind − upwind (site contribution). Negative deltas (upwind higher than downwind) are reported as measured.",
            "The delta is compared with each row's alert levels.",
            "Consecutive intervals above a level form one exceedance, with its start, end, duration, peak delta and the response step reached: 1 (15 min) check the instruments and identify the source, 2 (30 min) implement vapor and dust controls, 3 (45 min) modify operations and collect a confirmatory sample, 4 (60 min) stop emission-generating work.",
            "The data table has one row per interval with its average.",
            "Intervals without data from both sides at their last minutes are not evaluated."
          ]
        }
      },
      "level_names": {
        "pm10": [
          "Response level"
        ],
        "voc": [
          "Response level"
        ]
      },
      "level_info": {
        "pm10": [
          "NJDEP PAM response level: 150 µg/m³ (24-hour PM10 NAAQS) or lower"
        ],
        "voc": [
          "NJDEP PAM response level: site-specific, in ppb"
        ]
      },
      "level_hints": {
        "pm10": "Defaults to 150, the 24-hour PM10 NAAQS in µg/m³; use a lower site-specific response level when the PAM Calculator gives one.",
        "voc": "Enter the TVOC response level from the site's PAM plan (calculated with the NJDEP PAM Calculator). It's only needed if the site has volatile contaminants. Example from the guidance: 629 ppb."
      }
    },
    "cms_2567_report": {
      "add_category_label": "Add AECC category",
      "all_categories_added": "All available AECC categories have been added",
      "subcategories_title": "Subcategories",
      "view_thresholds": "View thresholds",
      "no_threshold_data": "No threshold data available",
      "groups": {
        "aecc-100": {
          "label": "Humidity Compliance",
          "description": "Monitor RH 20-60% in OR, sterile areas"
        },
        "aecc-200": {
          "label": "Temperature Compliance",
          "description": "Track room temperature per ASHRAE 170"
        },
        "aecc-300": {
          "label": "Pressure & Ventilation",
          "description": "Monitor pressure differentials"
        },
        "aecc-400": {
          "label": "Air Quality",
          "description": "Track PM, VOC, CO₂ levels"
        }
      }
    },
    "type_info": {
      "tooltip": "Learn about this report type",
      "air_quality": {
        "subheader": "Air quality of the selected devices against a standard",
        "description": "Shows how the selected devices performed against an air quality standard. Each parameter gets a status from the standard's ranges, and the devices get an overall air quality score.",
        "sections": [
          {
            "title": "Standard",
            "items": [
              "Built-in standards use their own parameters and ranges (good, moderate, poor).",
              "Custom: your own parameters, operators and alert levels. Parameters are rated Passed, Warning (alert level 1 of two) or Failed (alert level 2 or a single level), with no air quality score."
            ]
          },
          {
            "title": "View",
            "items": [
              "Basic: Summary per Parameter and Summary per Device, when at least one parameter has ranges.",
              "Extended: adds Analysis per Device, with a line chart per parameter and a data table of measurements for every device. Custom standards always include the charts; the data table needs Extended view."
            ]
          }
        ]
      },
      "perimeter_monitor": {
        "subheader": "Downwind compared with upwind around a work area",
        "description": "Compares downwind readings with upwind (background) readings to show the impact of on-site work. The delta (downwind − upwind) is checked against alert levels. Alert periods appear in the Impact Assessment, along with a data table and, with automatic wind detection, a wind rose.",
        "standard_title": "Standard",
        "standard": [
          "Custom: your own parameters, operators and alert levels."
        ],
        "standard_der10": "NYS DER-10: PM10 and VOC on 15-minute rolling averages, pre-filled with the DER-10 levels.",
        "standard_nj_pam": "NJDEP PAM: PM10 and TVOC on 15-minute averages with site-specific response levels.",
        "sections": [
          {
            "title": "Wind detection: Auto",
            "items": [
              "Upwind and downwind are chosen from the weather station's wind direction and the device locations: the furthest upwind device is the background, and the furthest downwind device is compared with it.",
              "Select the raw Wind Speed and Wind Direction (°) channels; the report averages them itself.",
              "Needs a weather station and at least two devices with a location."
            ]
          },
          {
            "title": "Wind detection: Manual",
            "items": [
              "You mark each device as upwind or downwind; the assignment doesn't follow the wind.",
              "Upwind devices are averaged into the background and downwind devices into the downwind reading. Each parameter needs both sides."
            ]
          },
          {
            "title": "Device locations",
            "items": [
              "A location set manually on the device is always used.",
              "Otherwise the last location recorded during the report period is used for the whole report. If there is none, the most recent one is used and the report lists the device in a note.",
              "Devices without a location aren't shown on the map or used for automatic wind detection."
            ]
          },
          {
            "title": "Floor negative deltas at zero",
            "items": [
              "When enabled, a negative delta (upwind higher than downwind) is reported as zero for concentration parameters. Signed parameters such as differential pressure and temperature are never floored.",
              "Custom reports only: standard reports show negative deltas as measured."
            ]
          }
        ]
      }
    },
    "settings": {
      "steps": {
        "report_type": "Report Type",
        "report_type_and_standard": "Report Type & Standard",
        "devices_and_wind": "Devices & Wind",
        "devices": "Devices",
        "parameters_and_alert_levels": "Parameters & Alert Levels",
        "data_aggregation": "Data Aggregation",
        "output": "Output",
        "aecc_categories": "AECC Compliance Categories"
      },
      "report_type": "Report type",
      "wind_detection": "Wind detection",
      "weather_station": "Weather station",
      "wind_speed": "Wind speed",
      "wind_direction": "Wind direction",
      "add_device": "Add device"
    }
  }
}
